/**
 * API service for backend communication
 */

import type { AnalysisInput, AnalysisResult, AnalysisError } from '@/types/analysis';
import { supabase } from '@/lib/supabase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Get authentication headers for API calls
 * Retrieves the Supabase session token and formats it for the backend
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  if (!supabase) {
    return headers;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch (error) {
    console.error('Error getting auth headers:', error);
  }

  return headers;
}

/**
 * Custom error class for rate limit exceeded responses
 */
export class RateLimitError extends Error {
  code: string;
  retryAfter: number;

  constructor(message: string, retryAfter: number = 3600) {
    super(message);
    this.name = 'RateLimitError';
    this.code = 'RATE_LIMIT_EXCEEDED';
    this.retryAfter = retryAfter;
  }
}

/**
 * Check response for rate limit error and throw RateLimitError if needed
 */
async function checkRateLimit(response: Response): Promise<void> {
  if (response.status === 429) {
    const data = await response.json().catch(() => ({}));
    const retryAfter = data.error?.retryAfter ||
                       parseInt(response.headers.get('Retry-After') || '3600', 10);
    const message = data.error?.message ||
                    'Too many requests. Please try again later.';
    throw new RateLimitError(message, retryAfter);
  }
}

export interface ApiResponse<T> {
  success: boolean;
  result?: T;
  error?: AnalysisError;
}

// =============================================================================
// Score analysis (music21 pipeline) — replaces browser-side Tonal.js detection
// =============================================================================

import type { AnalyzedChord } from '@/types/analysis';
import type { KeySegment } from '@/types/progression';

export interface AnalyzeScoreResponse {
  success: boolean;
  chords?: AnalyzedChord[];
  title?: string;
  composer?: string;
  key?: string;             // Tonic e.g. "C", "F#", "Bb"
  mode?: 'major' | 'minor';
  tempo?: number;
  timeSignature?: string;
  keySegments?: KeySegment[];
  /**
   * Version of the backend analyzer that produced this result. Stored with
   * the progression so a future cache can detect divergence — the bump rule
   * lives beside ANALYZER_VERSION in backend/services/score_analyzer.py.
   */
  analyzerVersion?: string;
  error?: string;
  error_kind?: string;
}

async function fileToBase64(file: File | Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // chunk to avoid call-stack issues with large files
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

/**
 * Analyze a MIDI or MusicXML score on the backend (music21 pipeline).
 * Returns the analyzed chord list + key/mode/tempo metadata.
 */
export async function analyzeScore(
  file: File | Blob,
  format: 'midi' | 'musicxml',
  options: { keyHint?: string; modeHint?: 'auto' | 'major' | 'minor' } = {}
): Promise<AnalyzeScoreResponse> {
  const base64 = await fileToBase64(file);
  const body: Record<string, unknown> = format === 'midi'
    ? { midi: base64 }
    : { musicxml: base64 };
  if (options.keyHint) body.keyHint = options.keyHint;
  if (options.modeHint) body.modeHint = options.modeHint;

  const response = await fetch(`${API_BASE_URL}/api/analyze/score`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });

  await checkRateLimit(response);

  if (response.status === 401) {
    throw new Error('Sign in to analyze scores. (Score analysis is auth-gated.)');
  }

  const data: AnalyzeScoreResponse = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Score analysis failed');
  }
  return data;
}

/**
 * Analyze multiple MIDI files as SATB voice parts on the backend.
 *
 * The browser base64-encodes each file and sends them as `midi_parts`.
 * The backend uses music21's stream-merge to combine them into a single
 * Score, then runs the same chordify + romanNumeralFromChord pipeline.
 *
 * Replaces the legacy browser-side merge path (Phase 3b migration).
 */
export async function analyzeScoreParts(
  parts: Array<{ file: File; voice?: string; label?: string }>,
  options: { keyHint?: string; modeHint?: 'auto' | 'major' | 'minor' } = {}
): Promise<AnalyzeScoreResponse> {
  const midiParts = await Promise.all(
    parts.map(async (p) => ({
      midi: await fileToBase64(p.file),
      voice: p.voice,
      label: p.label ?? p.file.name,
    }))
  );

  const body: Record<string, unknown> = { midi_parts: midiParts };
  if (options.keyHint) body.keyHint = options.keyHint;
  if (options.modeHint) body.modeHint = options.modeHint;

  const response = await fetch(`${API_BASE_URL}/api/analyze/score`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });

  await checkRateLimit(response);

  if (response.status === 401) {
    throw new Error('Sign in to analyze scores. (Score analysis is auth-gated.)');
  }

  const data: AnalyzeScoreResponse = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Score analysis failed (multi-file merge)');
  }
  return data;
}

// =============================================================================
// Corpus library (built-in public-domain score search)
// =============================================================================

export interface CorpusWork {
  id: string;
  title: string;
  composer: string;
  collection: string;
  parts: number;
  format: string;
}

export interface CorpusCollection {
  name: string;
  label: string;
  count: number;
}

export interface CorpusSearchResponse {
  works: CorpusWork[];
  total: number;
  collections: CorpusCollection[];
}

/**
 * Search the built-in public-domain score library (Bach, Palestrina, and
 * other Renaissance/Baroque/Classical composers). Auth is optional — the
 * endpoint accepts anonymous requests, so a 401 here is unexpected.
 */
export async function searchCorpus(
  q: string,
  opts: { collection?: string; limit?: number } = {}
): Promise<CorpusSearchResponse> {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (opts.collection) params.set('collection', opts.collection);
  if (opts.limit) params.set('limit', String(opts.limit));

  const response = await fetch(`${API_BASE_URL}/api/corpus/search?${params.toString()}`, {
    headers: await getAuthHeaders(),
  });

  await checkRateLimit(response);

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Corpus search failed');
  }

  return {
    works: data.works || [],
    total: data.total || 0,
    collections: data.collections || [],
  };
}

/**
 * Analyze a work from the built-in corpus library by id — skips the upload
 * step since the backend already holds the file. Returns the same
 * AnalyzeScoreResponse shape as analyzeScore/analyzeScoreParts.
 */
export async function analyzeCorpusWork(
  corpusId: string,
  options: { keyHint?: string; modeHint?: 'auto' | 'major' | 'minor' } = {}
): Promise<AnalyzeScoreResponse> {
  const body: Record<string, unknown> = { corpusId };
  if (options.keyHint) body.keyHint = options.keyHint;
  if (options.modeHint) body.modeHint = options.modeHint;

  const response = await fetch(`${API_BASE_URL}/api/analyze/score`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });

  await checkRateLimit(response);

  if (response.status === 401) {
    throw new Error('Sign in to analyze scores. (Score analysis is auth-gated.)');
  }

  const data: AnalyzeScoreResponse = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Score analysis failed');
  }
  return data;
}

/**
 * Upload audio file for analysis
 */
export async function uploadAudioFile(file: File): Promise<{ uploadId: string; expiresAt: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/upload`, {
    method: 'POST',
    body: formData,
  });

  await checkRateLimit(response);

  if (!response.ok) {
    throw new Error('Upload failed');
  }

  return response.json();
}

/**
 * Analyze audio and extract chords
 */
export async function analyzeAudio(input: AnalysisInput): Promise<ApiResponse<AnalysisResult>> {
  const response = await fetch(`${API_BASE_URL}/api/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type: input.type,
      youtubeUrl: input.youtubeUrl,
      videoId: input.videoId,
      uploadId: input.uploadId,
      startTime: input.startTime,
      endTime: input.endTime,
      keyHint: input.keyHint || 'auto',
      modeHint: input.modeHint || 'auto',
    }),
  });

  await checkRateLimit(response);

  return response.json();
}

/**
 * Check analysis status (for long-running jobs)
 */
export async function getAnalysisStatus(jobId: string): Promise<{
  status: 'processing' | 'complete' | 'failed';
  progress: number;
  stage: string;
  estimatedTimeRemaining?: number;
}> {
  const response = await fetch(`${API_BASE_URL}/api/analyze/status/${jobId}`);
  await checkRateLimit(response);
  return response.json();
}

/**
 * Types for suggestion API
 */
export interface SimpleChord {
  root: string;
  quality: string;
  extensions?: Record<string, boolean>;
}

export interface SuggestionData {
  id: string;
  technique: string;
  targetChordId?: string;
  fromChord: SimpleChord;
  toChord: SimpleChord;
  rationale: string;
  examples: string[];
  relevanceScore: number;
}

export interface SuggestResponse {
  success: boolean;
  suggestions?: SuggestionData[];
  error?: string;
}

/**
 * Get chord refinement suggestions from the API
 */
export async function suggestRefinements(
  intent: string,
  chords: SimpleChord[],
  key: string,
  mode: string
): Promise<SuggestResponse> {
  const response = await fetch(`${API_BASE_URL}/api/suggest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ intent, chords, key, mode }),
  });

  await checkRateLimit(response);

  if (!response.ok) {
    throw new Error('Suggestion request failed');
  }
  return response.json();
}

/**
 * Types for deconstruction API
 */
export type LayerType = 'skeleton' | 'sevenths' | 'suspensions' | 'extensions' | 'alterations';

export interface DeconstructStep {
  stepNumber: number;
  stepName: string;
  description: string;
  chords: SimpleChord[];
  layerType?: LayerType;
  modifiedIndices?: number[];
  romanNumerals?: string;
}

export interface DeconstructResponse {
  success: boolean;
  steps?: DeconstructStep[];
  error?: string;
}

/**
 * Deconstruct a chord progression into evolutionary steps with AI-generated explanations
 */
export async function deconstructProgression(
  chords: SimpleChord[],
  key: string,
  mode: string,
  songTitle?: string,
  composer?: string
): Promise<DeconstructResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/deconstruct`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      chords,
      key,
      mode,
      songTitle,
      composer,
    }),
  });

  await checkRateLimit(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Deconstruction request failed');
  }
  return response.json();
}

/**
 * Types for critique API
 */
export type CritiqueIssueType =
  | 'parallel_fifths'
  | 'parallel_octaves'
  | 'unresolved_tension'
  | 'voice_crossing'
  | 'large_leap'
  | 'doubled_leading_tone'
  | 'spacing_issue'
  | 'missing_resolution';

export type IssueSeverity = 'error' | 'warning' | 'suggestion';

export interface CritiqueIssue {
  id: string;
  type: CritiqueIssueType;
  severity: IssueSeverity;
  title: string;
  description: string;
  chordIds: string[];
  voice?: 'soprano' | 'alto' | 'tenor' | 'bass';
  suggestion?: string;
}

export interface CritiqueResponse {
  issues: CritiqueIssue[];
  summary: string;
  score: number;
  timestamp: string;
}

/**
 * Get harmonic critique for a chord progression
 * Requires authentication - uses Supabase session token (GRE-172)
 */
export async function critiqueProgression(
  chords: SimpleChord[],
  key: string,
  mode: string
): Promise<CritiqueResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/critique`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      chords,
      key,
      mode,
    }),
  });

  await checkRateLimit(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Critique request failed');
  }

  const data = await response.json();
  return {
    ...data,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Types for research API (GRE-100)
 */
export interface UncertainChord {
  offset: number;
  pitches: string[];
  currentLabel?: string;
  confidence: number;
  context?: string;
}

export interface ResearchSource {
  url: string;
  title: string;
  snippet: string;
  relevanceScore: number;
  chordsDiscussed: string[];
}

export interface ResearchSuggestion {
  chordOffset: number;
  suggestedLabel: string;
  reasoning: string;
  confidence: number;
  sourceRefs: number[];
}

export interface ResearchResponse {
  success: boolean;
  sources?: ResearchSource[];
  suggestions?: ResearchSuggestion[];
  summary?: string;
  error?: string;
}

/**
 * Research uncertain harmonies using AI with web search (GRE-100)
 *
 * When automated analysis has low confidence, this function calls the
 * backend to search for scholarly analyses and provide informed suggestions.
 */
export async function researchUncertainHarmonies(
  pieceTitle?: string,
  composer?: string,
  measureRange?: string,
  uncertainChords: UncertainChord[] = [],
  key?: string,
  mode?: string
): Promise<ResearchResponse> {
  const response = await fetch(`${API_BASE_URL}/api/research`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pieceTitle,
      composer,
      measureRange,
      uncertainChords,
      key,
      mode,
    }),
  });

  await checkRateLimit(response);

  if (!response.ok) {
    throw new Error('Research request failed');
  }

  return response.json();
}

/**
 * Types for generation API (GRE-120)
 */
export interface GenerateRequest {
  intent: string;
  mode: 'vibe' | 'text' | 'arc' | 'reference';
  key: string;
  musicalMode: string;
  chordCount: number;
}

export interface GeneratedChordData {
  id: string;
  scaleDegree: number;
  quality: string;
  extensions?: Record<string, boolean>;
  voices: {
    soprano: string;
    alto: string;
    tenor: string;
    bass: string;
  };
  narrative: string;
  narrativeFunction: 'setup' | 'build' | 'climax' | 'resolution' | 'bridge' | 'transition';
}

export interface GenerateResponse {
  success: boolean;
  chords?: GeneratedChordData[];
  narrative?: string;
  error?: string;
}

/**
 * Request for chord insight analysis
 */
export interface ChordInsightRequest {
  chords: SimpleChord[];
  selectedIndices: number[];
  key: string;
  mode: string;
  songTitle?: string;
  composer?: string;
}

/**
 * Response from chord insight analysis
 */
export interface ChordInsightResponse {
  success: boolean;
  insight?: string;
  harmonicFunction?: string;
  suggestions?: string[];
  error?: string;
}

/**
 * Vertical slice from polyphonic analysis
 */
export interface VerticalSlice {
  beat: number;
  duration: number;
  pitches: string[];
  voices: string[];
}

/**
 * Magic moment detected in the score (suspension, false relation, etc.)
 */
export interface MagicMoment {
  type: 'suspension' | 'false_relation' | 'picardy_third' | 'bare_fifth';
  beat: number;
  description: string;
  voices?: string[];
}

/**
 * Request for vertical slice extraction
 */
export interface ExtractSlicesRequest {
  musicxml: string;
  key_hint?: string;
  include_magic_moments?: boolean;
}

/**
 * Response from vertical slice extraction
 */
export interface ExtractSlicesResponse {
  success: boolean;
  reduction?: {
    piece_name: string;
    composer: string;
    key: string;
    mode: string;
    slices: VerticalSlice[];
    magic_moments: MagicMoment[];
  };
  error?: string;
}

/**
 * Generate a chord progression from narrative intent (GRE-120)
 *
 * Creates a new progression based on vibes, text, narrative arcs, or references.
 * This is different from refinement which modifies existing chords.
 */
export async function generateProgression(
  intent: string,
  mode: 'vibe' | 'text' | 'arc' | 'reference',
  key: string,
  musicalMode: string,
  chordCount: number = 8
): Promise<GenerateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      intent,
      mode,
      key,
      musicalMode,
      chordCount,
    }),
  });

  await checkRateLimit(response);

  if (!response.ok) {
    throw new Error('Generation request failed');
  }

  return response.json();
}

/**
 * Extract vertical slices from MusicXML for Renaissance polyphony analysis
 */
export async function extractVerticalSlices(
  musicxml: string,
  keyHint?: string,
  includeMagicMoments: boolean = true
): Promise<ExtractSlicesResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/extract-slices`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      musicxml,
      key_hint: keyHint,
      include_magic_moments: includeMagicMoments,
    }),
  });
  await checkRateLimit(response);
  return response.json();
}

/**
 * Get deep AI insight for selected chord(s)
 */
export async function getChordInsight(
  chords: SimpleChord[],
  selectedIndices: number[],
  key: string,
  mode: string,
  songTitle?: string,
  composer?: string
): Promise<ChordInsightResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/chord-insight`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      chords,
      selectedIndices,
      key,
      mode,
      songTitle,
      composer,
    }),
  });
  await checkRateLimit(response);
  return response.json();
}
